import { defineStore } from "pinia";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withLoading } from "~/helpers/withLoading";
import {
  GetUserRefreshTaskStatus200,
  type WebModelGetAppLicense,
  WebModelGetAppLicenseLicenseType,
  WebModelGetAppLicenseStatus,
  type WebModelGetLicenseCounts,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  createAppLicenseFromTicketId,
  createUserRefreshTask,
  findAppLicenses,
  getLicenseCounts,
  getUserRefreshTaskStatus,
  makeLicensePrivate,
  makeLicensePublic,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { useUserStore } from "~/stores/userstore";
import groupBy from "~/helpers/groupBy";
import { untilAsync } from "~/helpers/untilAync";
import { importLicensesViaLicenseOrderXmlFile } from "~/model/multipartRequests";

interface State {
  userLicenses: WebModelGetAppLicense[];
  companyLicenses: WebModelGetAppLicense[];
  groupedCompanyLicenses: GroupedLicenses[];
  companyMemberLicenses: WebModelGetAppLicense[];
  groupedCompanyMemberLicenses: GroupedLicenses[];
  licenseCounts: WebModelGetLicenseCounts | undefined;
}

export type GroupedLicenses = WebModelGetAppLicense & {
  allLicenses: WebModelGetAppLicense[];
};

export const useLicensesStore = defineStore("licenses", {
  // convert to a function
  state: (): State => ({
    userLicenses: [],
    companyLicenses: [],
    groupedCompanyLicenses: [],
    companyMemberLicenses: [],
    groupedCompanyMemberLicenses: [],
    licenseCounts: undefined,
  }),
  getters: {
    getUserAndCompanyLicensesForApp: (state: State) => {
      return (appId: number): WebModelGetAppLicense[] => {
        const arr = [];
        arr.push(
          ...state.userLicenses.filter((license) => license.appId === appId),
        );
        arr.push(
          ...state.companyLicenses.filter((license) => license.appId === appId),
        );
        return arr;
      };
    },
    getUserAndCompanyUnusedLicensesForApp: (state: State) => {
      return (appId: number): WebModelGetAppLicense[] => {
        const arr = [];
        arr.push(
          ...state.userLicenses
            .filter((license) => license.appId === appId)
            .filter(
              (license) =>
                license.status === WebModelGetAppLicenseStatus.UNINSTALLED,
            ),
        );
        arr.push(
          ...state.companyLicenses
            .filter((license) => license.appId === appId)
            .filter(
              (license) =>
                license.status === WebModelGetAppLicenseStatus.UNINSTALLED,
            ),
        );
        return arr;
      };
    },
    getGroupedLicensesForMyLicensePool: (state: State): GroupedLicenses[] => {
      const filtered = state.userLicenses
        // filter out licenses where deviceId is not null or undefined
        .filter((license) => license.deviceId == null || !license.deviceId)
        .filter(
          (license) =>
            license.licenseType === WebModelGetAppLicenseLicenseType.PAID,
        )
        .filter(
          (license) =>
            license.status === WebModelGetAppLicenseStatus.UNINSTALLED,
        );
      console.debug(filtered.length, "licenses found for user");
      return groupBy(filtered, (license) => license.appId);
    },
    getLicensesForLicenseOptionId: (state: State) => {
      return (licenseOptionId: number): WebModelGetAppLicense[] => {
        const licensesToReturn: WebModelGetAppLicense[] = [];
        for (const license of state.userLicenses) {
          if (
            license.licenseOption?.id &&
            license.licenseOption.id === licenseOptionId
          ) {
            licensesToReturn.push(license);
          }
        }
        return licensesToReturn;
      };
    },
    getLicensesForCompanyPublicPool: (state): WebModelGetAppLicense[] => {
      return (
        state.companyLicenses
          // filter out licenses where deviceId is not null or undefined
          .filter((license) => license.deviceId == null || !license.deviceId)
          .filter(
            (license) =>
              license.licenseType === WebModelGetAppLicenseLicenseType.PAID,
          )
          .filter(
            (license) =>
              license.status === WebModelGetAppLicenseStatus.UNINSTALLED,
          )
      );
    },
  },
  actions: {
    async loadLicensesForUser() {
      const userStore = useUserStore();
      if (!userStore.authUser?.uuid) {
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findAppLicenses({ "user-uuid": userStore.authUser?.uuid }),
          ),
        ),
      );
      console.debug("loadLicensesForUser: ", data, error);
      if (data) {
        this.userLicenses = data;
      }
    },

    async importLicenseFromTicketId(ticketId: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(createAppLicenseFromTicketId(ticketId))),
      );
      console.debug("importLicenseFromTicketId: ", data, error);
      if (data) {
        return true;
      }
      return false;
    },

    async importLicenseOrderXmlFile(xmlFile: File, importForCompany: boolean) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            importLicensesViaLicenseOrderXmlFile(xmlFile, { importForCompany }),
          ),
        ),
      );
      console.debug("importLicenseOrderXmlFile: ", data, error);
      if (error) {
        return false;
      }
      return data;
    },

    async moveLicenseToCompany(licenseId: number) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(makeLicensePublic(licenseId))),
      );
      console.debug("moveLicenseToCompany: ", data, error);
      if (data) {
        const promises: Promise<void>[] = [
          this.loadLicensesForUser(),
          this.loadPublicLicensesForCompany(),
        ];
        if (useUserStore().isCompanyAdmin) {
          promises.push(this.loadMemberLicensesForCompany());
        }
        await Promise.all(promises);
        return true;
      }
      return false;
    },

    async moveLicenseToUser(licenseId: number) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(makeLicensePrivate(licenseId))),
      );
      console.debug("moveLicenseToUser: ", data, error);
      if (data) {
        await Promise.all([
          this.loadLicensesForUser(),
          this.loadPublicLicensesForCompany(),
        ]);
        return true;
      }
      return false;
    },

    async loadPublicLicensesForCompany() {
      const userStore = useUserStore();
      if (!userStore.authUser?.uuid) {
        return;
      }
      if (!userStore.authUser.companyId) {
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findAppLicenses({
              "user-uuid": userStore.authUser?.uuid,
              "company-id": userStore.authUser?.companyId,
              public: true,
            }),
          ),
        ),
      );
      console.debug("loadLicensesForCompany: ", data, error);
      if (data) {
        this.companyLicenses = data;
        this.groupedCompanyLicenses = this.reduceToGroupedCompanyLicenses(data);
      }
    },

    async loadMemberLicensesForCompany() {
      const userStore = useUserStore();
      if (!userStore.authUser?.uuid) {
        return;
      }
      if (!userStore.authUser.companyId) {
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findAppLicenses({
              "user-uuid": userStore.authUser?.uuid,
              "company-id": userStore.authUser?.companyId,
              public: false,
            }),
          ),
        ),
      );
      console.debug("loadMemberLicensesForCompany: ", data, error);
      if (data) {
        this.companyMemberLicenses = data;
        this.groupedCompanyMemberLicenses =
          this.reduceToGroupedCompanyMemberLicenses(data);
      }
    },

    async loadLicenseCounts() {
      const userStore = useUserStore();
      if (!userStore.authUser?.uuid) {
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getLicenseCounts())),
      );
      console.debug("loadLicenseCounts: ", data, error);
      if (data) {
        this.licenseCounts = data;
      }
    },

    async createRefreshTask() {
      const userStore = useUserStore();
      if (!userStore.authUser?.uuid) {
        return;
      }
      const { error } = await withErrorHandling(
        withErrorMessage(createUserRefreshTask(userStore.authUser?.uuid)),
      );
      if (!error) {
        await untilAsync(
          async () => {
            try {
              if (!userStore.authUser?.uuid) {
                return;
              }
              const response = await getUserRefreshTaskStatus(
                userStore.authUser?.uuid,
              );
              console.debug("getUserRefreshTaskStatus: " + response.data);
              switch (response.data) {
                case GetUserRefreshTaskStatus200.SUCCEEDED:
                case GetUserRefreshTaskStatus200.FAILED:
                  return true;
                default:
                  return false;
              }
            } catch (err) {
              console.warn("Error polling for refresh task status: " + err);
              return true; // cancel polling on error
            }
          },
          1000,
          300000,
        ); // 300000 = 5 minutes max wait time
        await this.loadLicensesForUser();
      }
    },

    /**
     * - filters on no device
     * - de-dupes by (licenseOption.id + clientCount)
     * - stores flat list into `companyLicenses`
     * - returns grouped list by appId (optional convenience for UI)
     */
    reduceToGroupedCompanyLicenses(
      licensesFromLicensePool: WebModelGetAppLicense[],
    ): GroupedLicenses[] {
      const unbound = licensesFromLicensePool
        .map((item) => ({ ...item }))
        .filter((item) => item.deviceId == null || !item.deviceId);

      console.debug(
        "reduceToGroupedCompanyLicenses: ",
        unbound.length,
        "licenses found before reduce",
      );
      return groupBy(
        unbound,
        (l) =>
          `${l.licenseOption?.id ?? "no-license-option"}|${l.clientCount ?? "no-client-count"}`,
      );
    },

    /**
     * Member licenses variant
     * - de-dupes by (licenseOption.id + clientCount + userUuid)
     * - returns grouped list by appId (optional convenience for UI)
     */
    reduceToGroupedCompanyMemberLicenses(
      licensesFromLicensePool: WebModelGetAppLicense[],
    ): GroupedLicenses[] {
      const unbound = licensesFromLicensePool
        .map((item) => ({ ...item }))
        .filter((item) => item.deviceId == null || !item.deviceId)
        .filter((item) => item.userUuid !== useUserStore().authUserUuid);

      console.debug(
        "reduceToGroupedCompanyMemberLicenses: ",
        unbound.length,
        "licenses found before reduce",
      );

      return groupBy(
        unbound,
        (l) =>
          `${l.licenseOption?.id ?? "no-license-option"}|${l.clientCount ?? "no-client-count"}|${l.userUuid ?? "no-user-uuid"}`,
      );
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
